import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { clientAccountApi } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";

const Auth = () => {
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const { setClientName } = useAuth();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.email || !form.password || (mode === 'register' && (!form.name || !form.confirmPassword))) {
      setError('Please fill in all required fields.');
      return;
    }
    if (mode === 'register' && form.password !== form.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setError('');
    setSubmitting(true);
    if (mode === 'login') {
      try {
        const account = await clientAccountApi.login({ email: form.email, password: form.password });
        const clientName = typeof account.client === 'string' ? '' : account.client.name;
        if (!clientName) {
          setError('Client not found for this account.');
          return;
        }
        setClientName(clientName);
        setForm(prev => ({ ...prev, password: '' }));
        navigate('/client-vendor');
        return;
      } catch (err: any) {
        setError(err?.message || 'Invalid email or password.');
      } finally {
        setSubmitting(false);
      }
    } else {
      setSubmitting(false);
      setError('Self-registration is disabled. Please contact your provider to create an account.');
    }
  };

  return (
    <div className="auth-full-bg">
      <div className="auth-split-container">
        {/* Left: Login or Welcome */}
        <div className={`auth-side auth-side-left ${mode === 'login' ? 'active' : ''}`}>  
          <div className="auth-content">
            {/* <div className="auth-brand">
              <span>Doctor <span style={{color:'#8bc34a'}}>System</span></span>
            </div> */}
            {mode === 'login' ? (
              <>
                <h2 className="auth-title">Sign In</h2>
                <p className="auth-desc">Welcome back! Log in to manage your clinic smartly.</p>
                <form onSubmit={handleSubmit} className="auth-form">
                  <div className="auth-field">
                    <label >Email</label>
                    <input id="email" name="email" value={form.email} onChange={handleChange} placeholder="you@example.com" />
                  </div>
                  <div className="auth-field">
                    <label htmlFor="password">Password</label>
                    <input type="password" id="password" name="password" value={form.password} onChange={handleChange} placeholder="Password" />
                  </div>
                  {error && <div className="auth-error">{error}</div>}
                  <button className="auth-btn" type="submit" disabled={submitting}>
                    {submitting ? 'Signing in...' : 'Sign In'}
                  </button>
                </form>
                <div className="auth-toggle">
                  Don't have an account?{' '}
                  <span className="auth-link" onClick={() => setMode('register')}>Register</span>
                </div>
              </>
            ) : (
              <div className="auth-welcome">
                <h2 className="auth-title">Welcome Back!</h2>
                <p className="auth-desc">Already have an account? <span className="auth-link" onClick={() => setMode('login')}>Sign In</span></p>
              </div>
            )}
          </div>
        </div>
        {/* Right: Register or Welcome */}
        <div className={`auth-side auth-side-right ${mode === 'register' ? 'active' : ''}`}>  
          <div className="auth-content">
            <div className="auth-brand auth-brand-mobile">
              <span>Doctor <span style={{color:'#8bc34a'}}>System</span></span>
            </div>
            {mode === 'register' ? (
              <>
                <h2 className="auth-title">Create Account</h2>
                <p className="auth-desc">Join the smart healthcare revolution. Register your clinic today!</p>
                <form onSubmit={handleSubmit} className="auth-form">
                  <div className="auth-field">
                    <label htmlFor="name">Name</label>
                    <input type="text" id="name" name="name" value={form.name} onChange={handleChange} placeholder="Your Name" />
                  </div>
                  <div className="auth-field">
                    <label htmlFor="email">Email</label>
                    <input type="email" id="email" name="email" value={form.email} onChange={handleChange} placeholder="you@example.com" />
                  </div>
                  <div className="auth-field">
                    <label htmlFor="password">Password</label>
                    <input type="password" id="password" name="password" value={form.password} onChange={handleChange} placeholder="Password" />
                  </div>
                  <div className="auth-field">
                    <label htmlFor="confirmPassword">Confirm Password</label>
                    <input type="password" id="confirmPassword" name="confirmPassword" value={form.confirmPassword} onChange={handleChange} placeholder="Confirm Password" />
                  </div>
                  {error && <div className="auth-error">{error}</div>}
                  <button className="auth-btn" type="submit">Register</button>
                </form>
                <div className="auth-toggle">
                  Already have an account?{' '}
                  <span className="auth-link" onClick={() => setMode('login')}>Sign In</span>
                </div>
              </>
            ) : (
              <div className="auth-welcome">
                <h2 className="auth-title">Join Us!</h2>
                <p className="auth-desc">Create your account to get started.</p>
              </div>
            )}
          </div>
        </div>
      </div>
      <style>{`
        .auth-full-bg {
          width: 100vw;
          background: linear-gradient(120deg, #e3f7fa 0%, #f8f9fa 100%);
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .auth-split-container {
          width: 100vw;
          min-height: 100vh;
          display: flex;
          flex-direction: row;
          align-items: stretch;
          justify-content: center;
        }
        .auth-side {
          flex: 1 1 0;
          display: flex;
          align-items: center;
          justify-content: center;
          background: transparent;
          transition: background 0.3s;
          min-width: 0;
          position: relative;
        }
        .auth-side-left {
          background: linear-gradient(120deg, #e3f7fa 0%, #f8f9fa 100%);
          border-right: 1.5px solid #e5e7eb;
        }
        .auth-side-right {
          background: #336666;
        }
        .auth-content {
          width: 100%;
          max-width: 400px;
          padding: 48px 36px 36px 36px;
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        .auth-brand {
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: 2rem;
          font-weight: 800;
          color: #336666;
          margin-bottom: 24px;
        }
        .auth-title {
          font-size: 2rem;
          font-weight: 800;
          color: #336666;
          margin-bottom: 10px;
          text-align: center;
        }
        .auth-side-right .auth-title,
        .auth-side-right .auth-desc,
        .auth-side-right .auth-link,
        .auth-side-right .auth-toggle {
          color: #fff !important;
        }
        .auth-side-right .auth-btn {
          background: #8bc34a;
          color: #fff;
        }
        .auth-side-right .auth-btn:hover {
          background: #689f38;
        }
        .auth-desc {
          color: #607d8b;
          font-size: 1.1rem;
          margin-bottom: 28px;
          text-align: center;
        }
        .auth-form {
          width: 100%;
        }
        .auth-field {
          width: 100%;
          margin-bottom: 18px;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
        }
        .auth-field label {
          font-size: 1rem;
          font-weight: 600;
          color: #607d8b;
        }
        .auth-side-right .auth-field label {
          color: #fff;
        }
        .auth-field input {
          width: 100%;
          padding: 12px 16px;
          border-radius: 8px;
          border: 1px solid #e5e7eb;
          font-size: 1.1rem;
          background: #f8f9fa;
          color: #333;
          outline: none;
          transition: border 0.2s;
        }
        .auth-field input:focus {
          border: 1.5px solid #8bc34a;
        }
        .auth-error {
          color: #e57373;
          background: #ffebee;
          border-radius: 8px;
          padding: 8px 12px;
          margin-bottom: 12px;
          width: 100%;
          text-align: center;
          font-size: 1rem;
        }
        .auth-btn {
          width: 100%;
          background: #8bc34a;
          color: #fff;
          border: none;
          border-radius: 30px;
          padding: 14px 0;
          font-size: 1.15rem;
          font-weight: 700;
          letter-spacing: 1px;
          cursor: pointer;
          margin-top: 8px;
          box-shadow: 0 2px 8px rgba(139,195,74,0.08);
          transition: background 0.2s;
        }
        .auth-btn:hover {
          background: #689f38;
        }
        .auth-toggle {
          margin-top: 24px;
          color: #607d8b;
          font-size: 1.05rem;
          text-align: center;
        }
        .auth-side-right .auth-toggle {
          color: #fff;
        }
        .auth-link {
          color: #8bc34a;
          font-weight: 700;
          cursor: pointer;
          text-decoration: underline;
          margin-left: 4px;
        }
        .auth-side-right .auth-link {
          color: #ffd600;
        }
        .auth-welcome {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          width: 100%;
        }
        .auth-illustration {
          width: 180px;
          margin-top: 24px;
        }
        .auth-brand-mobile {
          display: none;
        }
        @media (max-width: 900px) {
          .auth-split-container {
            flex-direction: column;
          }
          .auth-side {
            border: none !important;
            min-height: 320px;
          }
          .auth-side-left, .auth-side-right {
            background: #fff;
            border: none;
          }
          .auth-side-right {
            background: #336666;
          }
          .auth-brand {
            display: none;
          }
          .auth-brand-mobile {
            display: flex;
            margin-bottom: 18px;
          }
        }
        @media (max-width: 600px) {
          .auth-full-bg {
            min-height: 100vh;
            padding: 0;
          }
          .auth-split-container {
            flex-direction: column;
            min-height: 100vh;
            width: 100vw;
          }
          .auth-side {
            min-height: unset;
            padding: 0;
            width: 100vw;
            border: none !important;
          }
          .auth-content {
            padding: 24px 8px 18px 8px;
            max-width: 100vw;
          }
          .auth-title {
            font-size: 1.4rem;
          }
          .auth-desc {
            font-size: 1rem;
            margin-bottom: 18px;
          }
          .auth-field input {
            font-size: 1rem;
            padding: 10px 12px;
          }
          .auth-btn {
            font-size: 1rem;
            padding: 12px 0;
          }
          .auth-illustration {
            width: 100px;
            margin-top: 12px;
          }
        }
      `}</style>
    </div>
  );
};

export default Auth; 